import "./gate.css"
import LogoImage from "image/wLogo.png"
import React, { useEffect, useState } from 'react'
import { useNavigate } from "react-router-dom"
import ApiService from "service/apiService"
import LocalStorageService from "service/localStorageService"
import { hasShareInbox } from "service/shareInbox"

type Stage = "Ready" | "Loading" | "Error" | "Reject" | "Pass";
type AgentStatus = "checking" | "ok" | "fail";
type PostKind = "hdr" | "dim" | "ok" | "fail" | "busy" | "warn";
type PostLine = { t: string; k?: PostKind };

function agentLine(status: AgentStatus): PostLine {
    if (status === "ok") {
        return { t: "Agent service .................... [OK]", k: "ok" };
    }
    if (status === "fail") {
        return { t: "Agent service .................... [FAIL]", k: "fail" };
    }
    return { t: "Agent service ....................", k: "busy" };
}

function buildPost(stage: Stage, errorMsg: string, agent: AgentStatus): PostLine[] {
    const lines: PostLine[] = [
        { t: "Argon Modular BIOS v1.00", k: "hdr" },
        { t: "Copyright (C) Argon SMB Agent", k: "dim" },
        { t: "" },
        { t: "CPU Check ........................ [OK]", k: "ok" },
        { t: "Memory Test ...................... [OK]", k: "ok" },
        agentLine(agent),
        { t: "" },
    ];
    if (agent === "checking") {
        return lines;
    }
    if (agent === "fail") {
        lines.push({ t: "Agent unreachable", k: "fail" });
        return lines;
    }
    if (stage === "Ready") {
        lines.push({ t: "Waiting for input ...............", k: "busy" });
        lines.push({ t: "" });
        lines.push({ t: "Press ENTER to continue", k: "warn" });
        return lines;
    }
    lines.push({ t: "Waiting for input ............... [OK]", k: "ok" });
    if (stage === "Loading") {
        lines.push({ t: "Authenticating SMB ..............", k: "busy" });
        return lines;
    }
    if (stage === "Pass") {
        lines.push({ t: "Authenticating SMB .............. [OK]", k: "ok" });
        lines.push({ t: "Issuing session token ........... [OK]", k: "ok" });
        lines.push({ t: "Loading user profile ............ [OK]", k: "ok" });
        lines.push({ t: "Starting desktop ................", k: "busy" });
        return lines;
    }
    lines.push({ t: "Authenticating SMB .............. [FAIL]", k: "fail" });
    lines.push({ t: stage === "Reject" ? (errorMsg || "Access denied") : "System error", k: "fail" });
    return lines;
}

export default function Gate() {
    const [loginStage, setLoginStage] = useState<Stage>("Ready");
    const [agentStatus, setAgentStatus] = useState<AgentStatus>("checking");
    const [accountInput, setAccountInput] = useState("");
    const [passwordInput, setPasswordInput] = useState("");
    const [errorMsg, setErrorMsg] = useState("");
    const navigate = useNavigate();
    const showForm = agentStatus === "ok" && loginStage !== "Loading" && loginStage !== "Pass";

    useEffect(() => {
        let cancelled = false;
        void ApiService.ping()
            .then((res) => {
                if (cancelled) {
                    return;
                }
                const pong = res.data?.success && res.data?.data === "pong";
                setAgentStatus(pong ? "ok" : "fail");
            })
            .catch(() => {
                if (!cancelled) {
                    setAgentStatus("fail");
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const sendLogin = async () => {
        try {
            setLoginStage("Loading");
            const trimmedAccount = accountInput.trim();
            const trimmedPassword = passwordInput.trim();
            if (trimmedAccount.length < 4) {
                setLoginStage("Reject");
                setErrorMsg("please check your input");
                return;
            }
            if (trimmedPassword.length < 6) {
                setLoginStage("Reject");
                setErrorMsg("please check your input");
                return;
            }
            const response = await ApiService.userLogin(accountInput, passwordInput);
            if (response && response.status === 200) {
                if (response.data.success) {
                    setLoginStage("Pass");
                    const data = response.data.data;
                    LocalStorageService.saveSessionToken(data.sessionToken);
                    LocalStorageService.saveSessionUser({
                        username: data.username,
                        homePath: data.homePath,
                        smbHostRef: data.smbHostRef,
                        userProfile: data.userProfile || null,
                        profileLoaded: !!data.userProfile,
                    });
                    setTimeout(() => {
                        void hasShareInbox().then((pending) => {
                            navigate(pending ? '/?share=1' : '/');
                        });
                    }, 300);
                } else {
                    setLoginStage("Reject");
                    setErrorMsg(response.data.message || "Login failed");
                }
            } else {
                setLoginStage("Error");
            }
        } catch (e) {
            setLoginStage("Error");
        }
    };

    const onFieldKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.type === "keydown") {
            setLoginStage("Ready");
            setErrorMsg("");
            return;
        }
        if (e.key === "Enter") {
            void sendLogin();
        }
    };

    return (
        <div id="Gate">
            <div className="post">
                {buildPost(loginStage, errorMsg, agentStatus).map((line, i) => (
                    <p key={i} className={line.k || undefined}>{line.t || "\u00a0"}</p>
                ))}
            </div>

            {showForm && (
                <div className="mainBox">
                    <input
                        onKeyDown={onFieldKey}
                        onKeyUp={onFieldKey}
                        value={accountInput}
                        onChange={(e) => setAccountInput(e.target.value.trim())}
                        type="text"
                        placeholder="User"
                        autoComplete="username"
                        spellCheck={false}
                    />
                    <input
                        onKeyDown={onFieldKey}
                        onKeyUp={onFieldKey}
                        value={passwordInput}
                        onChange={(e) => setPasswordInput(e.target.value.trim())}
                        type="password"
                        placeholder="Password"
                        autoComplete="current-password"
                    />
                </div>
            )}

            <div className="BootLogo">
                <div>
                    <div className="BackDrop"></div>
                    <img className="icon" src={LogoImage} alt="" />
                </div>
            </div>
        </div>
    );
}
